import {
  type EnvironmentId,
  type OrchestrationShellSnapshot,
  type ServerConfig,
} from "@afar-research/contracts";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { AsyncResult, Atom } from "effect/unstable/reactivity";

import { EnvironmentRegistry } from "../connection/registry.ts";
import { clientReadiness } from "../connection/model.ts";
import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import {
  clientTransportEnvironmentOwner,
  ClientTransportResources,
  forkClientTransportSubscription,
} from "../connection/resources.ts";
import type { SynchronizedHostViewState } from "../synchronization/hostView.ts";
import type { EnvironmentRuntimeState } from "./environments.ts";
import { followStreamInEnvironment } from "./runtime.ts";

export type EnvironmentShellStatus = "unavailable" | "starting" | "synchronizing" | "ready";

export interface EnvironmentShellState {
  readonly snapshot: Option.Option<OrchestrationShellSnapshot>;
  readonly status: EnvironmentShellStatus;
  readonly error: Option.Option<string>;
}

const EMPTY_SHELL_STATE: EnvironmentShellState = {
  snapshot: Option.none(),
  status: "unavailable",
  error: Option.none(),
};

/**
 * Apply a connection readiness change. Readiness alone never discards a snapshot: the
 * last known projects and threads stay visible, marked stale by the new status, until
 * fresh data replaces them or the Environment is deregistered.
 */
export function applyShellReadiness(
  current: EnvironmentShellState,
  status: EnvironmentShellStatus,
): EnvironmentShellState {
  if (current.status === status) {
    return current;
  }
  return { snapshot: current.snapshot, status, error: Option.none() };
}

/**
 * Apply a synchronized host view. The host view resets its own snapshot whenever it
 * re-synchronizes, so an absent snapshot means "refetching", not "empty".
 */
export function applyShellHostView(
  current: EnvironmentShellState,
  view: SynchronizedHostViewState,
): EnvironmentShellState {
  if (Option.isNone(view.snapshot)) {
    return applyShellReadiness(current, "synchronizing");
  }
  return { snapshot: view.snapshot, status: view.phase, error: Option.none() };
}

export const makeEnvironmentShellState = Effect.fn("EnvironmentShellState.make")(function* () {
  const supervisor = yield* EnvironmentSupervisor;
  const resources = yield* ClientTransportResources;
  const ownerIdentity = clientTransportEnvironmentOwner(supervisor.target.environmentId);
  const state = yield* SubscriptionRef.make<EnvironmentShellState>(EMPTY_SHELL_STATE);

  /** Writing an unchanged state would wake every subscriber for nothing. */
  const updateShellState = (
    transition: (current: EnvironmentShellState) => EnvironmentShellState,
  ) =>
    Effect.gen(function* () {
      const current = yield* SubscriptionRef.get(state);
      const next = transition(current);
      if (next !== current) {
        yield* SubscriptionRef.set(state, next);
      }
    });

  const applySynchronizedView = Effect.fn("EnvironmentShellState.applySynchronizedView")(function* (
    view: SynchronizedHostViewState,
  ) {
    const readiness = clientReadiness(yield* SubscriptionRef.get(supervisor.state));
    if (readiness !== "ready") {
      return;
    }
    yield* updateShellState((current) => applyShellHostView(current, view));
  });

  yield* forkClientTransportSubscription(
    resources,
    {
      ownerIdentity,
      identity: "shell-state:host-view",
      lifetime: "retained-owner",
    },
    SubscriptionRef.changes(supervisor.session).pipe(
      Stream.switchMap(
        Option.match({
          onNone: () => Stream.empty,
          onSome: (session) => session.hostViewChanges,
        }),
      ),
      Stream.runForEach(applySynchronizedView),
    ),
  );
  yield* forkClientTransportSubscription(
    resources,
    {
      ownerIdentity,
      identity: "shell-state:readiness",
      lifetime: "retained-owner",
    },
    SubscriptionRef.changes(supervisor.state).pipe(
      Stream.runForEach((connectionState) =>
        updateShellState((current) =>
          applyShellReadiness(current, clientReadiness(connectionState)),
        ),
      ),
    ),
  );

  return state;
});

export function shellStateChanges(environmentId: EnvironmentId) {
  return followStreamInEnvironment(
    environmentId,
    Stream.unwrap(makeEnvironmentShellState().pipe(Effect.map(SubscriptionRef.changes))),
  );
}

export interface EnvironmentShellSummary {
  readonly hasSnapshot: boolean;
  readonly hasStartingShell: boolean;
  readonly hasSynchronizingShell: boolean;
  readonly hasReadyShell: boolean;
  readonly firstError: string | null;
  readonly latestSnapshotUpdatedAt: string | null;
}

const EMPTY_ENVIRONMENT_SHELL_SUMMARY: EnvironmentShellSummary = Object.freeze({
  hasSnapshot: false,
  hasStartingShell: false,
  hasSynchronizingShell: false,
  hasReadyShell: false,
  firstError: null,
  latestSnapshotUpdatedAt: null,
});

const EMPTY_SERVER_CONFIGS: ReadonlyMap<EnvironmentId, ServerConfig> = new Map();

function shellSummariesEqual(
  left: EnvironmentShellSummary,
  right: EnvironmentShellSummary,
): boolean {
  return (
    left.hasSnapshot === right.hasSnapshot &&
    left.hasStartingShell === right.hasStartingShell &&
    left.hasSynchronizingShell === right.hasSynchronizingShell &&
    left.hasReadyShell === right.hasReadyShell &&
    left.firstError === right.firstError &&
    left.latestSnapshotUpdatedAt === right.latestSnapshotUpdatedAt
  );
}

function mapsEqual<K, V>(left: ReadonlyMap<K, V>, right: ReadonlyMap<K, V>): boolean {
  if (left.size !== right.size) {
    return false;
  }
  for (const [key, value] of left) {
    if (right.get(key) !== value) {
      return false;
    }
  }
  return true;
}

export function createEnvironmentShellSummaryAtom(input: {
  readonly environmentEntriesValueAtom: Atom.Atom<EnvironmentRuntimeState>;
  readonly shellStateValueAtom: (environmentId: EnvironmentId) => Atom.Atom<EnvironmentShellState>;
}) {
  let previousSummary = EMPTY_ENVIRONMENT_SHELL_SUMMARY;
  return Atom.make((get) => {
    let hasSnapshot = false;
    let hasStartingShell = false;
    let hasSynchronizingShell = false;
    let hasReadyShell = false;
    let firstError: string | null = null;
    let latestSnapshotUpdatedAt: string | null = null;

    for (const environmentId of get(input.environmentEntriesValueAtom).entries.keys()) {
      const state = get(input.shellStateValueAtom(environmentId));
      hasStartingShell ||= state.status === "starting";
      hasSynchronizingShell ||= state.status === "synchronizing";
      hasReadyShell ||= state.status === "ready";
      if (firstError === null) {
        firstError = Option.getOrNull(state.error);
      }
      if (Option.isNone(state.snapshot)) {
        continue;
      }
      hasSnapshot = true;
      const updatedAt = state.snapshot.value.updatedAt;
      if (latestSnapshotUpdatedAt === null || updatedAt > latestSnapshotUpdatedAt) {
        latestSnapshotUpdatedAt = updatedAt;
      }
    }

    const next: EnvironmentShellSummary = {
      hasSnapshot,
      hasStartingShell,
      hasSynchronizingShell,
      hasReadyShell,
      firstError,
      latestSnapshotUpdatedAt,
    };
    if (shellSummariesEqual(previousSummary, next)) {
      return previousSummary;
    }
    previousSummary = next;
    return previousSummary;
  }).pipe(Atom.withLabel("environment-shell-summary"));
}

export function createEnvironmentServerConfigsAtom(input: {
  readonly environmentEntriesValueAtom: Atom.Atom<EnvironmentRuntimeState>;
  readonly serverConfigValueAtom: (environmentId: EnvironmentId) => Atom.Atom<ServerConfig | null>;
}) {
  let previousServerConfigs = EMPTY_SERVER_CONFIGS;
  return Atom.make((get) => {
    const next = new Map<EnvironmentId, ServerConfig>();
    for (const environmentId of get(input.environmentEntriesValueAtom).entries.keys()) {
      const config = get(input.serverConfigValueAtom(environmentId));
      if (config !== null) {
        next.set(environmentId, config);
      }
    }
    if (mapsEqual(previousServerConfigs, next)) {
      return previousServerConfigs;
    }
    previousServerConfigs = next;
    return previousServerConfigs;
  }).pipe(Atom.withLabel("environment-server-configs"));
}

export function createEnvironmentShellAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const stateAtom = Atom.family((environmentId: EnvironmentId) =>
    runtime.atom(shellStateChanges(environmentId), {
      initialValue: EMPTY_SHELL_STATE,
    }),
  );

  const stateValueAtom = Atom.family((environmentId: EnvironmentId) =>
    Atom.make((get) =>
      Option.getOrElse(AsyncResult.value(get(stateAtom(environmentId))), () => EMPTY_SHELL_STATE),
    ).pipe(Atom.withLabel(`environment-shell-state-value:${environmentId}`)),
  );

  return {
    stateAtom,
    stateValueAtom,
  };
}

export * from "./models.ts";
export * from "./shellCommands.ts";
export * from "./shellReducer.ts";
export * from "./snapshots.ts";
